import { EMPTY_ARR, EMPTY_SET } from '@dadohr/shared/domainTypes/emptyConstants';
import { IEmployeeAppInboxResponse } from '@dadohr/shared/domainTypes/experience';
import {
  PAEventTypeV2,
  ProductAnalyticsEventV2
} from '@dadohr/shared/domainTypes/productAnalytics';
import { IUserDenormalized } from '@dadohr/shared/domainTypes/user';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import {
  Button,
  Checkbox,
  Chip,
  MenuItem,
  MenuList,
  Popover,
  Tooltip,
  useTheme
} from '@material-ui/core';
import { compact, orderBy, sortBy, truncate, uniqBy } from 'lodash';
import { ChevronRight, ListFilter } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { ColoredChip } from '../../../../../../../components/ColoredChip';
import { TableSearch } from '../../../../../../../components/Table';
import { useModel } from '../../../../../../../hooks/useModel';
import { useUnsafeTranslation } from '../../../../../../../i18next';
import { FlexContainer } from '../../../../../../../layout/Flex';
import {
  useStringListQueryParam,
  useStringSetQueryParam
} from '../../../../../../../routes';
import { toFullName } from '../../../../../../../services/user';
import { Divider } from '../../../components/Divider';
import { TemplateOption } from '../types';

export const useSelectedUsersForInbox = () => {
  return useStringListQueryParam('u', EMPTY_ARR);
};

export const useSelectedTemplatesForInbox = () => {
  return useStringSetQueryParam('t', EMPTY_SET as Set<string>);
};

const getUniqueTpls = <X extends TemplateOption>(ds: X[]): TemplateOption[] => {
  return sortBy(
    uniqBy(
      ds.map((d) => ({
        templateId: d.templateId,
        templateTitle: d.templateTitle
      })),
      (d) => d.templateId
    ),
    (d) => d.templateTitle
  );
};

// Stable identity for a denormalized user. v2's IUserDenormalized originally
// dropped `publicId` (see new-main packages/shared deprecated/domainTypes/user.ts),
// which collapsed every participant into one "Me" entry in the People filter.
// new-main now emits publicId again, but this fallback keeps the filter working
// when the FE is on a build that predates the BE redeploy.
const identityKey = (u: IUserDenormalized): string =>
  u.publicId || u.email || `${u.firstName || ''} ${u.lastName || ''}`.trim();

const getUniqueUsers = <X extends { participant: IUserDenormalized }>(
  ds: X[],
  currentUser: IUserDenormalized,
  currentUserLabel: string
): {
  key: string;
  label: string;
}[] => {
  const currentKey = identityKey(currentUser);
  return orderBy(
    uniqBy(
      ds.map((d) => d.participant),
      identityKey
    ).map((u) => {
      const key = identityKey(u);
      return {
        label: key === currentKey ? currentUserLabel : toFullName(u),
        key
      };
    }),
    [(x) => (x.key === currentKey ? 0 : 1), (x) => x.label]
  );
};

export const useFilterOptionsForInbox = (data: IEmployeeAppInboxResponse) => {
  const { t } = useUnsafeTranslation();
  return useMemo(() => {
    const allItems = [...data.tasks, ...data.messages];
    return {
      userOptions: getUniqueUsers(allItems, data.user, t('Me')),
      tplOptions: getUniqueTpls(allItems)
    };
  }, [data, t]);
};

const FilterChip = ({
  selectedUsers,
  selectedTemplates,
  onClick,
  userOptions,
  tplOptions,
  disabled
}: {
  selectedUsers: string[];
  selectedTemplates: Set<string>;
  userOptions: {
    key: string;
    label: string;
  }[];
  tplOptions: TemplateOption[];
  onClick: () => void;
  disabled?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  const userLabel = useMemo(() => {
    return selectedUsers.length === 1
      ? userOptions.find((x) => x.key === selectedUsers[0])?.label || null
      : selectedUsers.length > 1
      ? `${selectedUsers.length} ${t('people')}`
      : null;
  }, [selectedUsers, userOptions, t]);
  const tplLabel = useMemo(() => {
    return selectedTemplates.size === 1
      ? tplOptions.find(
          (x) => x.templateId === selectedTemplates.values().next().value
        )?.templateTitle || null
      : selectedTemplates.size > 1
      ? `${selectedTemplates.size} ${t('experiences')}`
      : null;
  }, [selectedTemplates, tplOptions, t]);

  const labels = compact([userLabel, tplLabel]);
  const label = truncate(labels.length ? labels.join(' • ') : t('Filter'), {
    length: 40
  });
  return (
    <Chip
      icon={<ListFilter size={16} />}
      label={label}
      variant="outlined"
      disabled={disabled}
      classes={{
        root: css({
          color: `${theme.palette.primary.dark} !important`,
          borderColor: `${theme.palette.primary.dark} !important`,
          '> svg': {
            color: `${theme.palette.primary.dark} !important`
          }
        })
      }}
      onClick={onClick}
    />
  );
};

type FilterProps = {
  selectedUsers: string[];
  selectedTemplates: Set<string>;
  setSelectedUsers: (users: string[]) => void;
  setSelectedTemplates: (templates: Set<string>) => void;
  userOptions: {
    key: string;
    label: string;
  }[];
  tplOptions: TemplateOption[];
  trackEventV2: (event: ProductAnalyticsEventV2) => void;
};

const HEIGHT = 400;

const FilterMenuContainer = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: '190px 1fr',
  gridColumnGap: p.theme.spacing(0),
  maxHeight: HEIGHT,
  width: 580,
  overflow: 'hidden'
}));

const FilterMenuSectionsContainer = styled('div')((p) => ({
  display: 'flex',
  flexDirection: 'column',
  borderRight: p.theme.custom.border.standard,
  height: '100%',
  padding: `${p.theme.spacing(2)}px ${p.theme.spacing(0)}px`
}));

const SearchContainer = styled('div')((p) => ({
  padding: p.theme.spacing(2),
  paddingBottom: p.theme.spacing(1)
}));

const FilterSection = ({
  value,
  onChange,
  options,
  allLabel
}: {
  value: string[];
  onChange: (value: string[]) => void;
  options: {
    key: string;
    label: string;
  }[];
  allLabel: string;
}) => {
  const { t } = useUnsafeTranslation();
  const [q, setQ] = useState('');
  const filteredOptions = useMemo(() => {
    return options.filter((x) =>
      x.label.toLowerCase().includes(q.toLowerCase())
    );
  }, [options, q]);

  const toItem = (
    key: string,
    label: string,
    isSelected: boolean,
    onClick: () => void
  ) => {
    return (
      <MenuItem
        key={key}
        role="button"
        onClick={onClick}
        style={{ width: '100%' }}
      >
        <FlexContainer fullWidth>
          <Checkbox checked={isSelected} />
          <div>{truncate(label, { length: 40 })}</div>
        </FlexContainer>
      </MenuItem>
    );
  };

  return (
    <div>
      <SearchContainer>
        <TableSearch
          term={q}
          onChange={setQ}
          placeholder={t('Search by name')}
          autoFocus
          fullWidth
        />
      </SearchContainer>
      <MenuList
        style={{
          overflow: 'auto',
          maxHeight: HEIGHT - 72 - 56 /* height of what's above and below */
        }}
      >
        {!q && (
          <>
            {toItem('__ALL__', allLabel, value.length === 0, () =>
              onChange(EMPTY_ARR)
            )}
            <Divider margin={1} />
          </>
        )}
        {filteredOptions.map((x) => {
          const isSelected = value.includes(x.key);
          return toItem(x.key, x.label, isSelected, () =>
            onChange(
              isSelected ? value.filter((y) => y !== x.key) : [...value, x.key]
            )
          );
        })}
        {q && filteredOptions.length === 0 && (
          <MenuItem disabled>{t('No matches found')}</MenuItem>
        )}
      </MenuList>
    </div>
  );
};

const FilterMenu = ({
  selectedUsers,
  selectedTemplates,
  setSelectedUsers,
  setSelectedTemplates,
  userOptions,
  tplOptions,
  onClose,
  trackEventV2
}: FilterProps & {
  onClose: () => void;
}) => {
  const [section, setSection] = useState<'templates' | 'users'>(
    !selectedTemplates.size && selectedUsers.length ? 'users' : 'templates'
  );
  const [userModel, setUserModel] = useModel(selectedUsers);
  const [tplModel, setTplModel] = useModel(selectedTemplates);
  const { t } = useUnsafeTranslation();
  const toSectionMenuItem = (
    key: 'templates' | 'users',
    label: string,
    count: number
  ) => {
    const isSelected = section === key;
    return (
      <MenuItem
        role="button"
        onClick={() => setSection(key)}
        style={{
          width: '100%'
        }}
      >
        <FlexContainer fullWidth justifyContent="space-between">
          <FlexContainer justifyContent="flex-start">
            {isSelected ? <strong>{label}</strong> : <div>{label}</div>}
            {/* Visibility trick instead of not rendering to guarantee a consistent row height */}
            <div style={{ visibility: count > 0 ? 'visible' : 'hidden' }}>
              <ColoredChip
                color={'blue'}
                mode="light"
                width={count >= 100 ? 60 : count >= 10 ? 50 : 40}
                size="small"
              >
                {count}
              </ColoredChip>
            </div>
          </FlexContainer>
          <ChevronRight
            size={16}
            color={isSelected ? 'currentColor' : 'transparent'}
          />
        </FlexContainer>
      </MenuItem>
    );
  };
  return (
    <FilterMenuContainer>
      <FilterMenuSectionsContainer>
        {toSectionMenuItem('templates', t('Experiences'), tplModel.size)}
        {toSectionMenuItem('users', t('People'), userModel.length)}
      </FilterMenuSectionsContainer>
      <div>
        {section === 'users' && (
          <FilterSection
            value={userModel}
            onChange={setUserModel}
            options={userOptions}
            allLabel={t('All people')}
          />
        )}
        {section === 'templates' && (
          <FilterSection
            value={[...tplModel]}
            onChange={(v) => setTplModel(new Set(v))}
            options={tplOptions.map((x) => ({
              key: x.templateId,
              label: x.templateTitle
            }))}
            allLabel={t('All experiences')}
          />
        )}
        <FlexContainer
          justifyContent="flex-end"
          fullWidth
          paddingTop={1}
          paddingBottom={2}
          paddingLeft={2}
          paddingRight={2}
        >
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setSelectedUsers(EMPTY_ARR);
              setSelectedTemplates(EMPTY_SET as Set<string>);
              trackEventV2({
                eventType: PAEventTypeV2.OTHER,
                name: 'Status Navigation Filters Cleared',
                Explanation:
                  'An assignee cleared all filters from the combined inbox page of the employee app'
              });
              onClose();
            }}
          >
            Clear filters
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => {
              setSelectedUsers(userModel);
              setSelectedTemplates(tplModel);

              trackEventV2({
                eventType: PAEventTypeV2.OTHER,
                name: 'Status Navigation Filtered',
                Explanation:
                  'An assignee applied filters to the combined inbox page of the employee app',
                'Experiences Selected': tplModel.size === 0 ? 'All' : 'Subset',
                'Users Selected': userModel.length === 0 ? 'All' : 'Subset',
                'Number of Experiences Selected': tplModel.size || undefined,
                'Number of Users Selected': userModel.length || undefined
              });

              onClose();
            }}
          >
            Apply filters
          </Button>
        </FlexContainer>
      </div>
    </FilterMenuContainer>
  );
};

export const InboxFilters = ({
  selectedUsers,
  selectedTemplates,
  setSelectedUsers,
  setSelectedTemplates,
  userOptions,
  tplOptions,
  justifyContent = 'flex-end',
  disabled,
  disabledTooltip,
  trackEventV2
}: FilterProps & {
  justifyContent?: React.CSSProperties['justifyContent'];
  disabled?: boolean;
  disabledTooltip?: React.ReactNode;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  return (
    <FlexContainer justifyContent={justifyContent}>
      <Tooltip title={(disabled && disabledTooltip) || ''}>
        <div ref={ref}>
          <FilterChip
            selectedUsers={selectedUsers}
            selectedTemplates={selectedTemplates}
            onClick={() => setOpen(true)}
            userOptions={userOptions}
            tplOptions={tplOptions}
            disabled={disabled}
          />
        </div>
      </Tooltip>
      <Popover
        onClose={() => setOpen(false)}
        open={open}
        anchorEl={ref.current}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
      >
        <FilterMenu
          selectedUsers={selectedUsers}
          selectedTemplates={selectedTemplates}
          setSelectedUsers={setSelectedUsers}
          setSelectedTemplates={setSelectedTemplates}
          userOptions={userOptions}
          tplOptions={tplOptions}
          onClose={() => setOpen(false)}
          trackEventV2={trackEventV2}
        />
      </Popover>
    </FlexContainer>
  );
};
